import React, { useState, useEffect, useCallback, Suspense } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PageFallback from './PageFallback';
import {
  ChartLineUp, Buildings, CreditCard, CurrencyInr, Headset,
  GearSix, SignOut, List, X, Eye, Crown, GraduationCap,
} from '@phosphor-icons/react';
import api from '../lib/api';
import { toast } from 'sonner';

const NAV = [
  { to: '/super-admin',               label: 'Dashboard',     icon: ChartLineUp,  testid: 'nav-dashboard' },
  { to: '/super-admin/companies',     label: 'Companies',     icon: Buildings,    testid: 'nav-companies' },
  { to: '/super-admin/subscriptions', label: 'Subscriptions', icon: CreditCard,   testid: 'nav-subscriptions' },
  { to: '/super-admin/revenue',       label: 'Revenue',       icon: CurrencyInr,  testid: 'nav-revenue' },
  { to: '/super-admin/support',       label: 'Support',       icon: Headset,      testid: 'nav-support' },
  { to: '/super-admin/training',      label: 'Training Videos', icon: GraduationCap, testid: 'nav-training' },
  { to: '/super-admin/settings',      label: 'Settings',      icon: GearSix,      testid: 'nav-settings' },
];

/**
 * SuperAdminLayout — fully separate dark-themed shell for the SaaS console.
 * Has its own sidebar (NO factory operation modules) and impersonation banner
 * when the super-admin is currently "logged in as" a tenant.
 */
export default function SuperAdminLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [impersonation, setImpersonation] = useState(null);

  const loadImpersonation = useCallback(async () => {
    try {
      const r = await api.get('/admin/impersonation-state');
      setImpersonation(r.data || null);
    } catch { setImpersonation(null); }
  }, []);

  useEffect(() => { loadImpersonation(); }, [loadImpersonation, location.pathname]);

  // Listen for impersonation changes triggered from any component (e.g., the
  // Companies tab's Impersonate button). Avoids a stale banner.
  useEffect(() => {
    const handler = () => loadImpersonation();
    window.addEventListener('impersonation-changed', handler);
    return () => window.removeEventListener('impersonation-changed', handler);
  }, [loadImpersonation]);

  const exitImpersonation = async () => {
    try {
      await api.post('/admin/exit-impersonate');
      toast.success('Impersonation ended');
      await loadImpersonation();
      navigate('/super-admin');
    } catch (_e) { toast.error('Failed to exit impersonation'); }
  };

  const isActive = (to) => location.pathname === to ||
    (to !== '/super-admin' && location.pathname.startsWith(to));

  const Sidebar = () => (
    <div className="h-full flex flex-col bg-[#0E0F12] text-stone-100" data-testid="super-admin-sidebar">
      <div className="px-5 py-5 border-b border-white/5 flex items-center gap-3">
        <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-amber-300 to-amber-500 flex items-center justify-center shadow-lg">
          <Crown size={18} weight="fill" className="text-stone-900" />
        </div>
        <div>
          <p className="text-[11px] tracking-[0.2em] text-amber-400 font-bold uppercase">Cut2Pro</p>
          <p className="text-sm font-bold leading-tight">SaaS Console</p>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-3 px-2">
        {NAV.map(({ to, label, icon: Icon, testid }) => {
          const active = isActive(to);
          return (
            <Link key={to} to={to} data-testid={testid}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 text-sm transition-all
                ${active ? 'bg-amber-400/10 text-amber-300 ring-1 ring-amber-400/20'
                         : 'text-stone-400 hover:text-stone-100 hover:bg-white/5'}`}
              onClick={() => setOpen(false)}>
              <Icon size={18} weight={active ? 'fill' : 'regular'} />
              <span className="font-medium">{label}</span>
            </Link>
          );
        })}
      </nav>

      <div className="border-t border-white/5 p-3 flex items-center gap-3">
        <div className="h-9 w-9 rounded-full bg-amber-500/20 text-amber-300 flex items-center justify-center font-bold text-sm">
          {(user?.name || 'A').charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold truncate">{user?.name || 'Admin'}</p>
          <p className="text-[10px] text-stone-500 uppercase tracking-wider">Super-admin</p>
        </div>
        <button onClick={logout} title="Sign out" data-testid="sa-logout"
          className="h-9 w-9 rounded-lg flex items-center justify-center text-stone-400 hover:text-red-300 hover:bg-red-400/10 transition-all">
          <SignOut size={16} />
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-[#16171B]" data-testid="super-admin-shell">
      <aside className="hidden lg:flex lg:w-60 flex-col">
        <Sidebar />
      </aside>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-64 bg-[#0E0F12] shadow-2xl"><Sidebar /></aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        {impersonation?.impersonating && (
          <div data-testid="impersonation-banner" className="bg-amber-500 text-stone-900 px-4 sm:px-6 py-2 flex items-center gap-3 sticky top-0 z-30 shadow">
            <Eye size={18} weight="fill" />
            <p className="text-xs sm:text-sm flex-1 font-semibold">
              Logged in as <span className="font-bold">{impersonation.company_name}</span> (full edit) —
              all changes are live.
            </p>
            <button onClick={() => navigate('/dashboard')}
              data-testid="impersonation-go-tenant"
              className="text-xs px-3 py-1 rounded-full bg-stone-900 text-amber-300 hover:bg-stone-800 font-semibold">
              Open Tenant App
            </button>
            <button onClick={exitImpersonation}
              data-testid="impersonation-exit"
              className="text-xs px-3 py-1 rounded-full bg-white/40 hover:bg-white/60 text-stone-900 font-semibold">
              Exit
            </button>
          </div>
        )}

        <header className="bg-[#1A1C20] border-b border-white/5 px-4 sm:px-6 py-3 flex items-center justify-between sticky top-0 z-20">
          <button className="lg:hidden p-2 rounded-lg hover:bg-white/5 text-stone-300"
            onClick={() => setOpen(true)} data-testid="sa-mobile-menu">
            <List size={20} />
          </button>
          <div className="flex-1">
            <p className="text-[10px] tracking-[0.25em] text-stone-500 uppercase">SaaS Control Panel</p>
            <h1 className="text-base sm:text-lg font-bold text-stone-100" data-testid="sa-page-title">
              {NAV.find(n => isActive(n.to))?.label || 'Dashboard'}
            </h1>
          </div>
          <button onClick={() => navigate('/super-admin/companies')}
            data-testid="sa-quick-companies"
            className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-stone-200 text-xs">
            <Buildings size={14} /> All companies
          </button>
        </header>

        <main className="flex-1 overflow-y-auto bg-[#16171B] text-stone-200 p-4 sm:p-6">
          <Suspense fallback={<PageFallback />}>
            {children}
          </Suspense>
        </main>
      </div>
    </div>
  );
}
